import React, { useEffect, useState, useMemo } from 'react';
import { getAllTranscripts, TranscriptDocument } from '../services/api';
import { scenarios, scenarioList } from '../services/scenarios';
import './ResultsPage.css';

const ResultsPage: React.FC = () => {

  const [transcripts, setTranscripts] = useState<TranscriptDocument[]>([]);
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptDocument | null>(null);
  const [scenarioFilter, setScenarioFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filteredTranscripts = useMemo(() => {
    let filtered = transcripts;
    if (scenarioFilter !== 'all') {
      filtered = transcripts.filter(transcript => transcript.scenario?.id === scenarioFilter);
    }
    // Sort by updatedAt, newest first
    return filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [transcripts, scenarioFilter]);

  useEffect(() => {
    const fetchTranscripts = async () => {
      try {
        const data = await getAllTranscripts();
        setTranscripts(data);
      } catch (err) {
        setError('Failed to fetch transcripts');
        console.error('Error fetching transcripts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTranscripts();
  }, []);

  const handleTranscriptSelect = (transcript: TranscriptDocument) => {
    setSelectedTranscript(transcript);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getProxyAudioUrl = (audioBlobUrl: string) => {
    // Extract blob name from the full URL
    // URL format: https://storageaccount.blob.core.windows.net/container/blobname
    const urlParts = audioBlobUrl.split('/');
    const blobName = urlParts.slice(4).join('/'); // Everything after container name
    return `${process.env.REACT_APP_API_BASE_URL}/getAudio?blobName=${encodeURIComponent(blobName)}`;
  };

  const getScenarioDisplayInfo = (transcript: TranscriptDocument) => {
    if (transcript.scenario) {
      const scenario = scenarios[transcript.scenario.id];
      return {
        title: scenario?.title || transcript.scenario.title,
        id: transcript.scenario.id
      };
    }
    return { title: 'Onbekend scenario', id: 'unknown' };
  };

  if (loading) {
    return <div className="results-page loading">Loading transcripts...</div>;
  }

  if (error) {
    return <div className="results-page error">Error: {error}</div>;
  }

  return (
    <div className="results-page">
      <div className="results-header">
        <h1>Results</h1>
        <div className="filter-container">
          <label htmlFor="scenario-filter">Filter op scenario:</label>
          <select 
            id="scenario-filter"
            value={scenarioFilter} 
            onChange={(e) => setScenarioFilter(e.target.value)}
            className="scenario-filter"
          >
            <option value="all">Alle scenario's</option>
            {scenarioList.map(scenario => (
              <option key={scenario.id} value={scenario.id}>
                {scenario.title}
              </option>
            ))}
            <option value="unknown">Onbekend scenario</option>
          </select>
        </div>
      </div>
      
      <div className="results-container">
        <div className="transcripts-list">
          <h2>Sessions ({filteredTranscripts.length})</h2>
          {filteredTranscripts.length === 0 ? (
            <p>Geen transcripts gevonden{scenarioFilter !== 'all' ? ' voor dit scenario' : ''}.</p>
          ) : (
            <div className="transcript-items">
              {filteredTranscripts.map((transcript) => {
                const scenarioInfo = getScenarioDisplayInfo(transcript);
                return (
                  <div
                    key={transcript.sessionId}
                    className={`transcript-item ${
                      selectedTranscript?.sessionId === transcript.sessionId ? 'selected' : ''
                    }`}
                    onClick={() => handleTranscriptSelect(transcript)}
                  >
                    <div className="transcript-header">
                      <strong>{scenarioInfo.title}</strong>
                      <div className="session-id">{transcript.sessionId}</div>
                    </div>
                    <div className="transcript-meta">
                      <div>Datum: {formatDate(transcript.createdAt)}</div>
                      <div>Turns: {transcript.turns.length}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="transcript-content">
          {selectedTranscript ? (
            <div>
              <h2>{getScenarioDisplayInfo(selectedTranscript).title}</h2>
              <div className="session-info">
                <p><strong>Session ID:</strong> {selectedTranscript.sessionId}</p>
                <p><strong>Created:</strong> {formatDate(selectedTranscript.createdAt)}</p>
                <p><strong>Total Turns:</strong> {selectedTranscript.turns.length}</p>
              </div>
              <div className="chat-conversation">
                {selectedTranscript.turns.map((turn, index) => (
                  <div key={`${selectedTranscript.sessionId}-turn-${index}`} className={`chat-turn ${turn.role}`}>
                    <div className="turn-header">
                      <span className="turn-role">{turn.role === 'user' ? 'User' : 'Assistant'}</span>
                      <span className="turn-timestamp">{new Date(turn.timestamp).toLocaleTimeString()}</span>
                    </div>
                    <div className="turn-content">
                      {turn.content}
                      {turn.error && (
                        <div className="error-details">
                          <strong>Error:</strong> {turn.error.message}
                          {turn.error.details && (
                            <div className="error-technical">
                              <small>Details: {turn.error.details}</small>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {turn.audioBlobUrl && (
                      <div className="turn-audio">
                        <audio controls key={`${selectedTranscript.sessionId}-audio-${index}`}>
                          <source src={getProxyAudioUrl(turn.audioBlobUrl)} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <p>Select a transcript to view the conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;